import { Grid, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { ClientConfigContext } from '../../../../clientConfigContext';
import BrandedCard from '../../../containers/BrandedCard/BrandedCard';
import TwoButtonContainer from '../../../containers/TwoButtonContainer';
import CollectDemographicsForm from './CollectDemographicsForm';

import { useMsal } from '@azure/msal-react';
import { AmplitudeEventType } from '../../../../../analytics/amplitudeEvents';
import { BrandProps } from '../../../../../clientConfig';
import { DemographicsAuthorizationEvidence } from '../../../../../types/authorizationEvidence';
import { LocationState } from '../../../../../types/reactTypes';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import { useValidatedUrl } from '../../../../hooks/useValidatedUrl';
import { sendEventByType } from '../../../../services/amplitudeClient';
import { usePIMS } from '../../../../services/pims/usePIMS';
import { getMyChartSamlSSOFromSystemAndBrand } from '../../../../util/clientSideMyChartUtil';
import getAccessToken from '../../../../util/getAccessToken';
import { redirectWithSoftLogout } from '../../../../util/sessionTimeout';
import CustomerServiceCard from '../../../containers/CustomerServiceCard';
import TransitionCard from '../../../containers/TransitionCard';
import withLogger, { ComponentLoggerProps } from '../../../HOC/WithLogger';
import { GeneralFormProps } from '../../types';

type CollectDemographicsQueryParams = {
    ehr_system: string;
    client_id: string;
    cross_region?: string;
    mobile?: string;
    redirect_uri?: string;
    redirect_url?: string;
    api_dep_condition?: string;
};

/**
 * The final page of the authorize by demographics flow. After completion, it returns back to the redirect_uri.
 * In the case of MyChart, we apply cross-EHR logic to redirect the user to the system they authorized in.
 * This might not result in going back to the same redirect_uri. For other applications, there is no concept of cross-EHR.
 */
function CollectDemographics({ brandInfo, log }: BrandProps & ComponentLoggerProps) {
    const [loading, setLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const location = useLocation<LocationState>();
    const { queryParams, error } = useQueryParams<CollectDemographicsQueryParams>();
    const { authorizeByDemographics } = usePIMS();
    const history = useHistory();
    const { instance, accounts } = useMsal();
    const { errors, control, formState, getValues } = useForm({
        mode: 'onChange',
    });
    const [phoneNumber, setPhoneNumber] = useState('');
    const [proveBypass, setProveBypass] = useState('');
    const clientConfiguration = useContext(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const [jwt, setJwt] = useState('');

    useEffect(() => {
        getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log).then((jwt) => {
            setJwt(jwt);
            sendEventByType(AmplitudeEventType.IdentityVerification_DemographicForm, jwt);
        });
    }, []);

    useEffect(() => {
        termsAccepted && sendEventByType(AmplitudeEventType.IdentityVerification_DemographicForm_Consent, jwt);
    }, [termsAccepted]);

    useEffect(() => {
        setPhoneNumber(location?.state?.phoneNumber);
        setProveBypass(location?.state?.proveBypass);
    }, [location]);

    /**
     * SPI-1801: Used to test the API Deprecation
     * TO-DO: Remove before production
     */
    const [apiDepCondition, setApiDepCondition] = useState<string | undefined>(undefined);
    useEffect(() => {
        setApiDepCondition(queryParams?.api_dep_condition);
    }, [queryParams?.api_dep_condition]);

    // An error occurred while fetching query parameters
    if (error !== undefined) {
        log.error(`CollectDemographics(): Error occurred - ${error}`);
        return <CustomerServiceCard />;
    }

    const {
        ehr_system: ehrSystem,
        client_id: clientId,
        cross_region,
        mobile,
        redirect_uri,
        redirect_url,
    } = queryParams;

    const isCrossRegion = cross_region === 'true';
    const isMobile = mobile === 'true';
    const redirectUrl = redirect_uri || redirect_url;

    if (!redirectUrl) {
        log.error('CollectDemographics(): Missing redirect_uri or redirect_url in query parameters');
        return <CustomerServiceCard />;
    }

    // Call PIMS AuthorizeByDemographics and then redirect back to the caller. If cross_region is set, then go back to an authorized system.
    const handleSubmit = async () => {
        const data = getValues();
        setLoading(true);
        const authorizationEvidence: DemographicsAuthorizationEvidence = {
            demographics: {
                firstName: data.firstName,
                lastName: data.lastName,
                gender: data.gender,
                dateOfBirth: data.birthdate,
                phoneNumber: phoneNumber,
                last4ssn: data.ssn,
                streetAddress: data.streetAddress,
                city: data.city,
                state: data.state,
                postalCode: data.zipCode,
                locationState: data.state,
            },
        };

        try {
            log.debug(
                `Authorize By Demographics: isMobile: ${isMobile}, ehrSystem: ${ehrSystem}, isCrossRegion: ${isCrossRegion}`,
            );
            const response = await authorizeByDemographics({
                authorizationEvidence,
                ehrSystem,
                proveBypass,
                brand: brandInfo.brand,
                clientId,
                apiDepCondition,
            });
            if (response.ok) {
                const body = await response.json();
                if (!body.authorizedSystem) {
                    log.error(`No AuthSystem, redirectUrl: ${redirectUrl}, ehrSystem: ${ehrSystem}`);
                    history.push('/error');
                    return;
                }

                // Cross Region is for MyChart-SAML flow. No other clients use set cross_region to true
                if (isCrossRegion) {
                    const mychartSamlSSOUrl = getMyChartSamlSSOFromSystemAndBrand(
                        clientConfiguration,
                        body.authorizedSystem,
                        brandInfo.brand,
                    );
                    if (mychartSamlSSOUrl) {
                        redirectWithSoftLogout(instance, mychartSamlSSOUrl, validateRedirectUrl);
                    } else {
                        log.error(
                            `Invalid MyChart SAML SSO URL: authorizedSystem: ${body.authorizedSystem}, brand: ${brandInfo.brand}`,
                        );
                        history.push('/error');
                    }
                } else {
                    log.info(`Go to redirectURL: ${redirectUrl}`);
                    redirectWithSoftLogout(instance, redirectUrl, validateRedirectUrl);
                }
            } else {
                log.error(
                    `Error occurred - ${response.status}${response.statusText ? ` -  ${response.statusText}` : ''}`,
                );
                history.push('/error');
            }
        } catch (err: any) {
            log.error(`Error occurred - ${err.message}`);
            history.push('/error');
        }
    };

    const handleBackButtonClick = () => {
        sendEventByType(AmplitudeEventType.IdentityVerification_DemographicForm_Back, jwt);
        history.push('/');
    };

    const { isDirty, isValid } = formState;

    return (
        <CollectDemographicsCard
            brandInfo={brandInfo}
            control={control}
            errors={errors}
            loading={loading}
            setTermsAccepted={setTermsAccepted}
            termsAccepted={termsAccepted}
            phoneNumber={phoneNumber}
            handleBackButtonClick={handleBackButtonClick}
            handleSubmit={handleSubmit}
            isDirty={isDirty}
            isValid={isValid}
        />
    );
}

export function CollectDemographicsCard({
    brandInfo,
    loading,
    control,
    errors,
    setTermsAccepted,
    termsAccepted,
    phoneNumber,
    handleBackButtonClick,
    handleSubmit,
    isDirty,
    isValid,
}: BrandProps &
    GeneralFormProps & {
        loading: boolean;
        setTermsAccepted: (terms: boolean) => void;
        termsAccepted: boolean;
        phoneNumber: string;
        handleBackButtonClick: () => void;
        handleSubmit: () => void;
        isDirty: boolean;
        isValid: boolean;
    }): JSX.Element {
    const { t } = useTranslation();

    if (loading) {
        return <TransitionCard title='Creating your account' />;
    } else {
        return (
            <div className={'displayFlex'}>
                <BrandedCard maxWidth={711} brand={brandInfo}>
                    {{
                        header: (
                            <>
                                <Typography variant='h2'>{t('Help us find your medical record')}</Typography>
                            </>
                        ),
                        content: (
                            <>
                                <CollectDemographicsForm
                                    control={control}
                                    errors={errors}
                                    setTermsAccepted={setTermsAccepted}
                                    brand={brandInfo}
                                    phoneNumber={phoneNumber}
                                />
                            </>
                        ),
                        actions: (
                            <>
                                <Grid container justify='space-around' item xs={12}>
                                    <TwoButtonContainer
                                        firstButtonProps={{
                                            variant: 'text',
                                            label: t('Back'),
                                            onClick: handleBackButtonClick,
                                            brand: brandInfo.brand,
                                        }}
                                        secondButtonProps={{
                                            variant: 'contained',
                                            disabled: !isDirty || !isValid || !termsAccepted,
                                            onClick: handleSubmit,
                                            type: 'button',
                                            label: t('Next'),
                                            brand: brandInfo.brand,
                                        }}
                                    />
                                </Grid>
                            </>
                        ),
                    }}
                </BrandedCard>
            </div>
        );
    }
}

export default withLogger(CollectDemographics, {
    eventSource: 'SubmitAuthorizeDemographics',
});
